import React, { useState, useMemo, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { getPdgFileSystem } from "../../../../data/origami/pdgFileSystem";

export default function PdgFileExplorer({
  onStartCopy,
  copyProgress = null,
  copiedFilePaths = [],
  myVariant,
  playerCount,
  onEjectUsb,
  usbEjected = false,
  socketRef,
  onOpenAuthModal,
  authUnlocked = true,
  onResetCopy,
  initialPath
}) {
  const [currentPath, setCurrentPath] = useState(initialPath || "/Desktop");
  const [viewMode, setViewMode] = useState("list");
  const [openFile, setOpenFile] = useState(null);
  const [draggedFile, setDraggedFile] = useState(null);
  // Track quel fichier CE joueur est en train de copier
  const [myCopyingFile, setMyCopyingFile] = useState(null);
  const [failDismissed, setFailDismissed] = useState(true);
  const [successDismissed, setSuccessDismissed] = useState(true);
  const expectedProofCount = [4, 5, 6].includes(Number(playerCount)) ? Number(playerCount) : 6;

  useEffect(() => {
    if (!initialPath) return;
    setCurrentPath(initialPath);
  }, [initialPath]);

  // Ref pour tracker le dernier timestamp de fail qu'on a vu
  const lastFailSeenRef = useRef(null);
  const lastSuccessSeenRef = useRef(null);
  const userDismissedSuccessRef = useRef(false);


  const PDG_FILE_SYSTEM = useMemo(() => {
    return getPdgFileSystem(expectedProofCount);
  }, [expectedProofCount]);

  const resolvePath = (path) => {
    const item = PDG_FILE_SYSTEM[path];
    return item?.type === "shortcut" ? item.target : path;
  };

  const currentFolder = useMemo(() => {
    const resolved = resolvePath(currentPath);
    return PDG_FILE_SYSTEM[resolved];
  }, [currentPath]);

  // Pour la clé USB, on ajoute dynamiquement les fichiers copiés
  const children = useMemo(() => {
    if (!currentFolder?.children) return [];

    const resolvedPath = resolvePath(currentPath);
    let childNames = [...currentFolder.children];

    // Si on est dans la clé USB, on ajoute les fichiers copiés
    if (resolvedPath === "/USB_DRIVE") {
      const alreadyThere = new Set(childNames);
      for (const filePath of copiedFilePaths) {
        const fileName = filePath.split("/").pop();
        if (!alreadyThere.has(fileName)) {
          // On ajoute le fichier depuis son emplacement original
          const originalItem = PDG_FILE_SYSTEM[filePath];
          if (originalItem) {
            childNames.push(filePath); // On utilise le path complet comme clé temporaire
          }
        }
      }
    }

    return childNames.map((childName) => {
      // Support pour les paths complets (fichiers copiés dans USB)
      const childPath = childName.startsWith("/")
        ? childName
        : `${resolvedPath}/${childName}`;
      const child = PDG_FILE_SYSTEM[childPath];
      return child ? { ...child, path: childPath, name: child.name || childName } : null;
    }).filter(Boolean);
  }, [currentFolder, currentPath, copiedFilePaths]);

  const navigateTo = (path) => {
    const item = PDG_FILE_SYSTEM[path];
    if (!item) return;
    if (item.type === "folder") setCurrentPath(path);
    else if (item.type === "shortcut") setCurrentPath(item.target);
    else if (item.type === "file") setOpenFile(item);
  };

  const goUp = () => {
    if (currentPath === "/") return;
    const parts = currentPath.split("/").filter(Boolean);
    parts.pop();
    setCurrentPath(parts.length > 0 ? "/" + parts.join("/") : "/");
  };

  const breadcrumbs = useMemo(() => {
    if (currentPath === "/") return [{ name: "Ce PC", path: "/" }];
    const parts = currentPath.split("/").filter(Boolean);
    const crumbs = [{ name: "Ce PC", path: "/" }];
    let acc = "";
    for (const part of parts) {
      acc += "/" + part;
      const item = PDG_FILE_SYSTEM[acc];
      crumbs.push({ name: item?.name || part, path: acc });
    }
    return crumbs;
  }, [currentPath]);

  const getIcon = (item) => {
    if (item.type === "folder") return "📁";
    if (item.type === "shortcut") return "🔗";
    if (item.name?.endsWith(".pdf")) return "📄";
    if (item.name?.endsWith(".xlsx") || item.name?.endsWith(".csv")) return "📊";
    if (item.name?.endsWith(".txt") || item.name?.endsWith(".md")) return "📝";
    return "📄";
  };

  const handleDragStart = (e, filePath) => {
    const item = PDG_FILE_SYSTEM[filePath];
    if (item?.type !== "file" || !item?.isProof) return;
    e.dataTransfer.effectAllowed = "copy";
    setDraggedFile(filePath);
  };

  const handleDragEnd = () => setDraggedFile(null);

  const handleDrop = (e, targetPath) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedFile) return;
    if (targetPath !== "/USB_DRIVE") { setDraggedFile(null); return; }
    if (copiedFilePaths.includes(draggedFile)) { setDraggedFile(null); return; }
    const alreadyCopying = copyProgress?.files?.some(
      (f) => f.path === draggedFile && f.status !== "failed"
    );
    if (alreadyCopying) { setDraggedFile(null); return; }

    const sourceItem = PDG_FILE_SYSTEM[draggedFile];
    setMyCopyingFile(draggedFile);

    // ✅ Forcer la suppression du fail immédiatement (avant réponse serveur)
    setFailDismissed(true);
    setSuccessDismissed(true);
    userDismissedSuccessRef.current = false;
    onStartCopy?.(draggedFile, sourceItem.name);
    setDraggedFile(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  // ✅ Savoir si ma copie est en cours
  const myFileCopyInfo = useMemo(() => {
    if (!myCopyingFile || !copyProgress?.files) return null;
    return copyProgress.files.find((f) => f.path === myCopyingFile) || null;
  }, [myCopyingFile, copyProgress]);

  const isInUsb = resolvePath(currentPath) === "/USB_DRIVE";
  const copyStatus = copyProgress?.status;

  useEffect(() => {
    if (!copyProgress) return;

    const status = copyProgress.status;
    const ts = copyProgress.timestamp;

    if (status === "failed") {
      setMyCopyingFile(null);
      if (ts && ts !== lastFailSeenRef.current) {
        lastFailSeenRef.current = ts;
        setFailDismissed(false);
      } else if (!ts) {
        setFailDismissed(false);
      }
    }

    // ✅ "copying" : on reset successDismissed mais PAS failDismissed
    // (failDismissed est déjà géré par handleDrop au moment du drop)
    if (status === "copying" || status === "idle") {
      setSuccessDismissed(true);
      userDismissedSuccessRef.current = false;
      // ❌ Ne pas toucher failDismissed ici
    }

    if (status === "success") {
      if (userDismissedSuccessRef.current) return;
      if (ts && ts !== lastSuccessSeenRef.current) {
        lastSuccessSeenRef.current = ts;
        setSuccessDismissed(false);
      } else if (!ts) {
        setSuccessDismissed(false);
      }
    }

    // ✅ Si le serveur dit "ejected", ne rien afficher (PdgOS gère ça)
    if (status === "ejected") {
      setFailDismissed(true);
      setSuccessDismissed(true);
      setMyCopyingFile(null);
    }
  }, [copyProgress?.status, copyProgress?.timestamp]);

  // ✅ Reset myCopyingFile quand copie terminée (séparé pour éviter les conflits)
  useEffect(() => {
    if (myFileCopyInfo?.status === "copied") {
      const t = setTimeout(() => setMyCopyingFile(null), 2000);
      return () => clearTimeout(t);
    }
  }, [myFileCopyInfo?.status]);


  // ============================
  // MODALE COPIE (pour le joueur qui copie — progress bar individuelle)
  // ============================
  const MyCopyModal = myCopyingFile && myFileCopyInfo ? ReactDOM.createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center pointer-events-none">
      <div
        className="pointer-events-auto w-[420px] rounded-lg overflow-hidden shadow-2xl"
        style={{
          background: "linear-gradient(135deg, #0f1623 0%, #111827 100%)",
          border: "1px solid rgba(99,102,241,0.4)",
          boxShadow: "0 0 40px rgba(99,102,241,0.15), inset 0 0 0 1px rgba(255,255,255,0.05)",
        }}
      >
        {/* Header barre de titre style Windows */}
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{ background: "linear-gradient(90deg, rgba(99,102,241,0.3), rgba(99,102,241,0.1))", borderBottom: "1px solid rgba(99,102,241,0.2)" }}
        >
          <div className="flex items-center gap-2">
            <span className="text-base">📋</span>
            <span className="text-xs font-semibold text-indigo-200 tracking-wider uppercase">
              Copie de fichier
            </span>
          </div>
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-slate-600" />
            <div className="w-3 h-3 rounded-full bg-slate-600" />
            <div className="w-3 h-3 rounded-full bg-red-500/60" />
          </div>
        </div>

        {/* Corps */}
        <div className="p-5">
          {/* Fichier source → destination */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 rounded bg-white/5 border border-white/10 px-3 py-2 text-xs text-slate-300 truncate">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Source</div>
              <div className="flex items-center gap-1.5">
                <span>📄</span>
                <span className="truncate">{PDG_FILE_SYSTEM[myCopyingFile]?.name}</span>
              </div>
            </div>
            <div className="text-indigo-400 text-lg flex-shrink-0">→</div>
            <div className="flex-1 rounded bg-emerald-500/10 border border-emerald-400/20 px-3 py-2 text-xs text-emerald-300 truncate">
              <div className="text-[10px] text-emerald-600 uppercase tracking-wider mb-1">Destination</div>
              <div className="flex items-center gap-1.5">
                <span>💾</span>
                <span>Clé USB (E:)</span>
              </div>
            </div>
          </div>

          {/* Progress bar principale */}
          <div className="mb-2">
            <div className="flex justify-between text-[11px] text-slate-400 mb-1.5">
              <span>Transfert en cours…</span>
              <span className="tabular-nums text-indigo-300">{myFileCopyInfo.progress ?? 0}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden relative">
              {/* Fond animé */}
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background: "repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(255,255,255,0.1) 8px, rgba(255,255,255,0.1) 10px)",
                  animation: "slide 1s linear infinite",
                }}
              />
              <div
                className="h-full rounded-full relative transition-all duration-500"
                style={{
                  width: `${myFileCopyInfo.progress ?? 0}%`,
                  background: "linear-gradient(90deg, rgba(99,102,241,0.9), rgba(139,92,246,0.9))",
                  boxShadow: "0 0 12px rgba(99,102,241,0.6)",
                }}
              />
            </div>
          </div>

          {/* Vitesse simulée */}
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>
              {myFileCopyInfo.status === "copied"
                ? "✅ Transfert terminé"
                : `Vitesse : ${(12.4 + Math.sin(Date.now() / 400) * 2).toFixed(1)} MB/s`}
            </span>
            <span>{PDG_FILE_SYSTEM[myCopyingFile]?.size ?? ""}</span>
          </div>
        </div>
      </div>
      <style>{`@keyframes slide { from { background-position: 0 0; } to { background-position: 20px 0; } }`}</style>
    </div>,
    document.body
  ) : null;

  // ============================
  // MODALE TIMER GLOBAL (visible par tous quand copie active)
  // ============================
  const GlobalTimerBanner = copyProgress && copyStatus === "copying" ? ReactDOM.createPortal(
    <div className="fixed top-0 left-0 right-0 z-[80] pointer-events-none">
      <div
        className="flex items-center justify-between px-6 py-2"
        style={{
          background: copyProgress.timeRemaining <= 5
            ? "linear-gradient(90deg, rgba(220,38,38,0.95), rgba(153,27,27,0.95))"
            : "linear-gradient(90deg, rgba(17,24,39,0.97), rgba(30,27,75,0.97))",
          borderBottom: copyProgress.timeRemaining <= 5
            ? "1px solid rgba(248,113,113,0.5)"
            : "1px solid rgba(99,102,241,0.3)",
          boxShadow: copyProgress.timeRemaining <= 5
            ? "0 0 30px rgba(220,38,38,0.4)"
            : "0 0 20px rgba(0,0,0,0.5)",
        }}
      >
        {/* Gauche : icône + label */}
        <div className="flex items-center gap-3">
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{
              background: copyProgress.timeRemaining <= 5 ? "#f87171" : "#818cf8",
              boxShadow: copyProgress.timeRemaining <= 5
                ? "0 0 8px #f87171"
                : "0 0 8px #818cf8",
              animation: "pulse 1s ease-in-out infinite",
            }}
          />
          <span
            className="text-xs font-semibold tracking-[0.2em] uppercase"
            style={{ color: copyProgress.timeRemaining <= 5 ? "#fca5a5" : "#a5b4fc" }}
          >
            Système de sécurité actif — effacement automatique imminent
          </span>
        </div>

        {/* Centre : fichiers copiés */}
        <div className="flex items-center gap-3">
          {(copyProgress.files || []).map((f, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full transition-all duration-300"
              title={f.name}
              style={{
                background: f.status === "copied" ? "#34d399" : "rgba(255,255,255,0.2)",
                boxShadow: f.status === "copied" ? "0 0 6px #34d399" : "none",
              }}
            />
          ))}
        </div>

        {/* Droite : compte à rebours */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] tracking-wider uppercase"
            style={{ color: copyProgress.timeRemaining <= 5 ? "#fca5a5" : "#64748b" }}>
            Effacement dans
          </span>
          <span
            className="font-mono font-black text-xl tabular-nums"
            style={{
              color: copyProgress.timeRemaining <= 5 ? "#f87171" : "#e2e8f0",
              textShadow: copyProgress.timeRemaining <= 5 ? "0 0 20px #f87171" : "none",
              minWidth: "2ch",
            }}
          >
            {copyProgress.timeRemaining}
          </span>
          <span className="text-xs text-slate-500">s</span>
        </div>
      </div>

      {/* Barre de progression du timer */}
      <div className="h-0.5 bg-white/5">
        <div
          className="h-full transition-all duration-1000"
          style={{
            width: `${(copyProgress.timeRemaining / 20) * 100}%`,
            background: copyProgress.timeRemaining <= 5
              ? "linear-gradient(90deg, #ef4444, #f87171)"
              : "linear-gradient(90deg, #6366f1, #818cf8)",
          }}
        />
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>,
    document.body
  ) : null;

  // ============================
  // MODALE ÉCHEC (pour tous, 3s puis disparaît)
  // ============================
  const FailureBanner = copyStatus === "failed" && !failDismissed ? ReactDOM.createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.85)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-10"
        style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.05) 2px, rgba(255,255,255,0.05) 4px)" }} />

      <div className="relative w-[480px] rounded-xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1a0a0a 0%, #0f0a0a 100%)",
          border: "1px solid rgba(239,68,68,0.4)",
          boxShadow: "0 0 60px rgba(239,68,68,0.3), inset 0 0 0 1px rgba(255,255,255,0.05)",
        }}>
        <div style={{ height: "3px", background: "linear-gradient(90deg, transparent, #ef4444, transparent)" }} />

        <div className="p-8 text-center">
          <div className="relative inline-block mb-4">
            <div className="text-5xl" style={{ filter: "drop-shadow(0 0 20px rgba(239,68,68,0.8))" }}>💾</div>
            <div className="absolute inset-0 flex items-center justify-center text-4xl"
              style={{ color: "#ef4444", fontWeight: 900, textShadow: "0 0 20px #ef4444" }}>✕</div>
          </div>

          <div className="text-lg font-black tracking-[0.15em] uppercase mb-2"
            style={{ color: "#f87171", textShadow: "0 0 15px rgba(239,68,68,0.6)" }}>
            Effacement sécurisé exécuté
          </div>

          <div className="text-sm text-slate-400 mb-6 leading-relaxed">
            Le système de sécurité a détecté une copie non autorisée.<br />
            <span className="text-red-400">Contenu de la clé USB effacé.</span>
          </div>

          <div className="text-left rounded-lg p-4 mb-6 font-mono text-xs"
            style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <div className="text-red-500">[SECURITY] Délai dépassé — effacement en cours</div>
            <div className="text-red-400">[SECURITY] Écrasement des secteurs USB… terminé</div>
            <div className="text-slate-500">[SYSTEM]   Drive E: effacé. 0 fichiers restants.</div>
            <div className="text-amber-400 mt-1">[SYSTEM]   Prêt pour une nouvelle tentative.</div>
          </div>

          {/* ✅ Bouton fermer */}
          <button
            onClick={() => setFailDismissed(true)}
            className="w-full rounded-lg px-4 py-3 text-sm font-bold transition"
            style={{
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.4)",
              color: "#fca5a5",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
          >
            Compris — Réessayer
          </button>
        </div>

        <div style={{ height: "3px", background: "linear-gradient(90deg, transparent, #ef4444, transparent)" }} />
      </div>
    </div>,
    document.body
  ) : null;


  // ============================
  // MODALE SUCCÈS + ÉJECTION
  // ============================
  // Menu contextuel clic droit sur la clé USB
  const [usbContextMenu, setUsbContextMenu] = useState(null); // { x, y } ou null

  // Handler clic droit sur la clé USB (à ajouter dans la sidebar)
  const handleUsbRightClick = (e) => {
    if (copyStatus !== "success") return;
    e.preventDefault();
    setUsbContextMenu({ x: e.clientX, y: e.clientY });
  };


  const SuccessReadyModal = copyStatus === "success" && !successDismissed ? ReactDOM.createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}>
      <div className="w-[440px] rounded-xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #051a0f 0%, #0a1f0d 100%)",
          border: "1px solid rgba(52,211,153,0.4)",
          boxShadow: "0 0 60px rgba(52,211,153,0.2), inset 0 0 0 1px rgba(255,255,255,0.04)",
        }}>
        <div style={{ height: "2px", background: "linear-gradient(90deg, transparent, #34d399, transparent)" }} />

        <div className="p-8 text-center">
          <div className="text-5xl mb-4"
            style={{ filter: "drop-shadow(0 0 20px rgba(52,211,153,0.8))" }}>💾</div>

          <div className="text-lg font-black tracking-[0.15em] uppercase mb-2"
            style={{ color: "#34d399", textShadow: "0 0 15px rgba(52,211,153,0.6)" }}>
            Transfert réussi
          </div>

          <div className="text-sm text-slate-400 mb-6 leading-relaxed">
            Les {expectedProofCount} preuves ont été copiées avec succès.<br />
            <span className="text-emerald-300 font-semibold">La clé USB est prête à être éjectée.</span>
          </div>

          <div className="text-left rounded-lg p-4 mb-6 font-mono text-xs"
            style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(52,211,153,0.15)" }}>
            <div className="text-emerald-400">[USB]     {expectedProofCount}/{expectedProofCount} fichiers transférés avec succès</div>
            <div className="text-emerald-300">[USB]     Vérification de l'intégrité… OK</div>
            <div className="text-slate-400">[SYSTEM]  Drive E: prêt pour le retrait</div>
            <div className="text-emerald-200 mt-1 font-semibold">[SYSTEM]  Éjection sécurisée possible.</div>
          </div>

          <div className="flex items-center justify-center gap-2 rounded-lg px-4 py-3 mb-5"
            style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.2)" }}>
            <span className="text-emerald-400 text-base">💡</span>
            <span className="text-xs text-emerald-300">
              Faites un <strong>clic droit</strong> sur <strong>Clé USB (E:)</strong> pour l'éjecter
            </span>
          </div>

          {/* Bouton fermer discret */}
          <button
            onClick={() => {
              userDismissedSuccessRef.current = true;
              setSuccessDismissed(true);
            }}
            className="w-full rounded-lg px-4 py-2 text-xs transition"
            style={{
              background: "rgba(52,211,153,0.08)",
              border: "1px solid rgba(52,211,153,0.15)",
              color: "#6ee7b7",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.15)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(52,211,153,0.08)"}
          >
            Fermer — accéder à l'explorateur
          </button>
        </div>

        <div style={{ height: "2px", background: "linear-gradient(90deg, transparent, #34d399, transparent)" }} />
      </div>
    </div>,
    document.body
  ) : null;

  // Menu contextuel natif simulé
  const UsbContextMenu = usbContextMenu ? ReactDOM.createPortal(
    <>
      {/* Overlay invisible pour fermer */}
      <div className="fixed inset-0 z-[89]" onClick={() => setUsbContextMenu(null)} />
      <div
        className="fixed z-[90] rounded-lg overflow-hidden shadow-2xl py-1"
        style={{
          top: usbContextMenu.y,
          left: usbContextMenu.x,
          background: "#1e293b",
          border: "1px solid rgba(255,255,255,0.15)",
          minWidth: "200px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        }}
      >
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/10">
          Clé USB (E:)
        </div>
        <button
          className="w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition flex items-center gap-3"
          onClick={() => { setUsbContextMenu(null); navigateTo("/USB_DRIVE"); }}
        >
          <span>📂</span> Ouvrir
        </button>
        <button
          className="w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-white/10 transition flex items-center gap-3"
          onClick={() => { setUsbContextMenu(null); navigateTo("/USB_DRIVE"); }}
        >
          <span>🔍</span> Explorer les fichiers
        </button>
        <div className="my-1 border-t border-white/10" />
        {/* <button
          className="w-full text-left px-4 py-2.5 text-sm text-emerald-300 hover:bg-emerald-500/10 transition flex items-center gap-3 font-semibold"
          onClick={() => {
            setUsbContextMenu(null);
            onEjectUsb?.();
          }}
        >
          <span>⏏</span> Éjecter Clé USB (E:)
        </button> */}
        <button
          className={[
            "w-full text-left px-4 py-2.5 text-sm transition flex items-center gap-3 font-semibold",
            copyStatus === "success"
              ? "text-emerald-300 hover:bg-emerald-500/10 cursor-pointer"
              : "text-slate-600 cursor-not-allowed opacity-40",
          ].join(" ")}
          disabled={copyStatus !== "success"}
          onClick={() => {
            if (copyStatus !== "success") return;
            setUsbContextMenu(null);
            onEjectUsb?.();
          }}
        >
          <span>⏏</span>
          {copyStatus === "success"
            ? "Éjecter Clé USB (E:)"
            : "Éjection non disponible — copie en cours"}
        </button>
      </div>
    </>,
    document.body
  ) : null;

  // ============================
  // MODALE FICHIER OUVERT
  // ============================
  const FileModal = openFile ? ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80">
      <div className="bg-slate-900 border border-white/20 rounded-lg w-[600px] max-h-[80vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-white/10">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <span>📄</span>
            <span>{openFile.name}</span>
          </div>
          <button
            onClick={() => setOpenFile(null)}
            className="w-6 h-6 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-400/40 flex items-center justify-center text-red-200 text-xs transition"
          >✕</button>
        </div>
        <div className="flex-1 p-6 overflow-auto">
          {openFile.content ? (
            <pre className="text-sm text-slate-200 whitespace-pre-wrap font-mono">{openFile.content}</pre>
          ) : (
            <div className="text-center text-slate-400">
              <div className="text-4xl mb-3">📄</div>
              <div className="text-sm">Aperçu non disponible</div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="flex h-full bg-slate-950 overflow-hidden">
        {/* SIDEBAR */}
        <div className="w-44 flex-shrink-0 bg-slate-900/60 border-r border-white/10 p-3 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Accès rapide</div>
          <div className="space-y-1">
            {[
              { path: "/", label: "Ce PC", emoji: "💻" },
              { path: "/Documents", label: "Documents", emoji: "📂" },
              { path: "/Desktop", label: "Bureau", emoji: "🖥️" },
              { path: "/Downloads", label: "Téléchargements", emoji: "⬇️" },
            ].map(({ path, label, emoji }) => (
              <button key={path} onClick={() => navigateTo(path)}
                className="w-full text-left px-2 py-2 rounded hover:bg-white/10 text-sm text-white flex items-center gap-2 transition">
                <span>{emoji}</span><span>{label}</span>
              </button>
            ))}

            <div className="my-2 border-t border-white/10" />

            {/* CLÉ USB — drop zone + indicateur de statut */}
            {!usbEjected && (
              <div
                onDrop={(e) => handleDrop(e, "/USB_DRIVE")}
                onDragOver={handleDragOver}
                onClick={() => navigateTo("/USB_DRIVE")}
                onContextMenu={handleUsbRightClick}
                className={[
                  "w-full px-2 py-2 rounded text-sm flex items-center gap-2 transition cursor-pointer select-none",
                  draggedFile ? "bg-emerald-500/20 border-2 border-emerald-400/60 text-emerald-200" : "hover:bg-white/10 text-emerald-200",
                  copyStatus === "success" ? "border border-emerald-400/60 shadow-[0_0_12px_rgba(52,211,153,0.3)]" : "",
                ].join(" ")}
              >
                <span>💾</span>
                <span>Clé USB (E:)</span>
                {copiedFilePaths.length > 0 && (
                  <span className="ml-auto text-[10px] bg-emerald-500/20 text-emerald-300 rounded-full px-1.5 py-0.5">
                    {copiedFilePaths.length}
                  </span>
                )}
                {copyStatus === "success" && (
                  <span className="text-[10px] text-emerald-400" title="Prête à éjecter">⏏</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ZONE PRINCIPALE */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Barre de navigation */}
          <div className="flex-shrink-0 bg-slate-900/60 border-b border-white/10 px-3 py-2 flex items-center gap-2">
            <button onClick={goUp} disabled={currentPath === "/"}
              className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs disabled:opacity-40 transition">
              ← Retour
            </button>
            <div className="flex items-center gap-1 flex-1 text-xs text-slate-300 overflow-hidden">
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={crumb.path}>
                  {idx > 0 && <span className="text-slate-500 flex-shrink-0">/</span>}
                  <button onClick={() => navigateTo(crumb.path)}
                    className="hover:text-white hover:underline transition truncate">{crumb.name}</button>
                </React.Fragment>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-white/10 rounded overflow-hidden flex-shrink-0">
              {["list", "grid"].map((mode) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={["px-2 py-1 text-xs transition",
                    viewMode === mode ? "bg-white/20 text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"
                  ].join(" ")}>
                  {mode === "list" ? "Liste" : "Grille"}
                </button>
              ))}
            </div>
          </div>

          {/* Bannière "glissez vers la clé USB" si copie pas encore démarrée */}
          {!copyProgress && !isInUsb && !usbEjected && (
            <div className="flex-shrink-0 px-3 py-1.5 bg-indigo-900/30 border-b border-indigo-400/20 text-xs text-indigo-300 flex items-center gap-2">
              <span>💡</span>
              <span>Glissez une preuve <strong>⭐</strong> vers <strong>Clé USB (E:)</strong> dans la barre latérale</span>
            </div>
          )}

          {/* Contenu du dossier */}
          <div className="flex-1 overflow-auto p-3">
            {/* ✅ Message spécial si clé USB vide */}
            {isInUsb && copiedFilePaths.length === 0 ? (
              <div className="text-center text-slate-500 mt-8"
                onDrop={(e) => handleDrop(e, "/USB_DRIVE")}
                onDragOver={handleDragOver}>
                <div className="text-4xl mb-3">💾</div>
                <div className="text-sm">Clé USB vide</div>
                <div className="text-xs text-slate-600 mt-1">Glissez les preuves ici</div>
              </div>
            ) : children.length === 0 ? (
              <div className="text-center text-slate-500 mt-8">
                <div className="text-4xl mb-3">📭</div>
                <div className="text-sm">Dossier vide</div>
              </div>
            ) : viewMode === "list" ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-white/10">
                    <th className="pb-2 font-medium">Nom</th>
                    <th className="pb-2 font-medium">Type</th>
                    <th className="pb-2 font-medium">Taille</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((child) => {
                    const isDraggable = child.type === "file" && child.isProof && !copiedFilePaths.includes(child.path);
                    const alreadyCopied = copiedFilePaths.includes(child.path);
                    return (
                      <tr key={child.path}
                        draggable={isDraggable}
                        onDragStart={(e) => isDraggable && handleDragStart(e, child.path)}
                        onDragEnd={handleDragEnd}
                        onDoubleClick={() => navigateTo(child.path)}
                        className={[
                          "border-b border-white/5 transition",
                          isDraggable ? "cursor-grab hover:bg-white/5" : "cursor-pointer hover:bg-white/5",
                          child.isProof ? "bg-indigo-500/5" : "",
                          alreadyCopied ? "opacity-50" : "",
                        ].join(" ")}>
                        <td className="py-2 pr-2">
                          <div className="flex items-center gap-2">
                            <span className="text-base flex-shrink-0">{getIcon(child)}</span>
                            <span className="text-white truncate">{child.name}</span>
                            {child.isProof && !alreadyCopied && (
                              <span className="text-indigo-400 text-[10px] flex-shrink-0">⭐ PREUVE</span>
                            )}
                            {alreadyCopied && (
                              <span className="text-emerald-400 text-[10px] flex-shrink-0">✅ COPIÉ</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 text-slate-400 text-xs">
                          {child.type === "folder" ? "Dossier" : child.type === "shortcut" ? "Raccourci" : "Fichier"}
                        </td>
                        <td className="py-2 text-slate-400 text-xs">{child.size || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {children.map((child) => {
                  const isDraggable = child.type === "file" && child.isProof && !copiedFilePaths.includes(child.path);
                  const alreadyCopied = copiedFilePaths.includes(child.path);
                  return (
                    <button key={child.path}
                      draggable={isDraggable}
                      onDragStart={(e) => isDraggable && handleDragStart(e, child.path)}
                      onDragEnd={handleDragEnd}
                      onDoubleClick={() => navigateTo(child.path)}
                      className={[
                        "flex flex-col items-center gap-2 p-3 rounded-lg border transition",
                        child.isProof ? "border-indigo-400/40 bg-indigo-500/10" : "bg-white/5 border-white/10",
                        "hover:bg-white/10",
                        alreadyCopied ? "opacity-50" : "",
                      ].join(" ")}>
                      <div className="text-3xl">{getIcon(child)}</div>
                      <div className="text-xs text-white text-center break-all">{child.name}</div>
                      {alreadyCopied && <span className="text-emerald-400 text-[10px]">✅</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Portals */}
      {GlobalTimerBanner}
      {MyCopyModal}
      {FailureBanner}
      {SuccessReadyModal}
      {UsbContextMenu}
      {FileModal}
    </>
  );
}